import { ReactNode, useState } from "react";
import { Navbar } from "./Navbar";
import { WhatsAppFloat } from "./WhatsAppFloat";
import { Button } from "./ui/button";
import newsletterService from "@/services/newsletterService";
import { useToast } from "@/hooks/use-toast";

interface LayoutProps {
  children: ReactNode;
  showNavbar?: boolean;
}

export const Layout = ({ children, showNavbar = true }: LayoutProps) => {
  const { toast } = useToast();
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [isSubscribing, setIsSubscribing] = useState(false);

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newsletterEmail.trim()) {
      toast({
        title: "Email requerido",
        description: "Por favor ingresa tu email",
        variant: "destructive",
      });
      return;
    }

    setIsSubscribing(true);

    try {
      const response = await newsletterService.subscribe(newsletterEmail);

      toast({
        title: "¡Suscripción exitosa!",
        description:
          response.message || "Gracias por suscribirte a nuestro newsletter",
        variant: "default",
      });

      // Limpiar el campo
      setNewsletterEmail("");
    } catch (error: any) {
      toast({
        title: "Error al suscribirse",
        description:
          error.message || "Ocurrió un error. Por favor intenta más tarde.",
        variant: "destructive",
      });
    } finally {
      setIsSubscribing(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FEFDFD] flex flex-col">
      {showNavbar && <Navbar />}
      <main className={`flex-1 ${showNavbar ? "pt-[64px] md:pt-[88px]" : ""}`}>
        {children}
      </main>

      <WhatsAppFloat />

      {/* Footer */}
      <footer className="bg-brown-light text-[#F8F6F4] py-20 px-2">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10 md:gap-8">
            <div className="flex flex-col items-start md:items-start">
              <img
                src="/logo.png"
                alt="Vivi con Amor y Arte"
                className="h-20 md:h-24 w-auto mb-6 animate-fade-in"
              />
              <p className="text-[#F8F6F4]/90 text-base mb-6">
                Creando agendas con amor y arte para ayudarte a planificar tu
                vida con estilo.
              </p>
            </div>

            <div>
              <h3 className="font-playfair text-lg font-bold mb-4 text-[#F8F6F4]">
                Enlaces
              </h3>
              <ul className="space-y-2">
                <li>
                  <a
                    href="/products"
                    className="text-[#F8F6F4]/90 hover:text-primary transition-colors animated-underline"
                  >
                    Catálogo
                  </a>
                </li>
                <li>
                  <a
                    href="/about"
                    className="text-[#F8F6F4]/90 hover:text-primary transition-colors animated-underline"
                  >
                    Nosotros
                  </a>
                </li>
                {/* <li>
                  <a
                    href="/privacidad"
                    className="text-[#F8F6F4]/90 hover:text-primary transition-colors animated-underline"
                  >
                    Política de Privacidad
                  </a>
                </li>
                <li>
                  <a
                    href="/terminos"
                    className="text-[#F8F6F4]/90 hover:text-primary transition-colors animated-underline"
                  >
                    Términos y Condiciones
                  </a>
                </li> */}
              </ul>
            </div>

            <div>
              <h3 className="font-playfair text-lg font-bold mb-4 text-[#F8F6F4]">
                Contacto
              </h3>
              <ul className="space-y-2 text-[#F8F6F4]/90">
                <li>
                  <a
                    href="https://wa.me/50761568575"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 hover:text-primary transition-colors"
                  >
                    <svg className="w-5 h-5" fill="#F3D6C6" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
                    </svg>
                    WhatsApp: +507 61568575
                  </a>
                </li>
                <li>
                  <a
                    href="/contact"
                    className="flex items-center gap-2 hover:text-primary transition-colors"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="#F3D6C6"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                      <polyline points="22,6 12,13 2,6" />
                    </svg>
                    Envíanos un mensaje
                  </a>
                </li>
                <li className="flex items-center gap-2">
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="#F3D6C6"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                  Ciudad de Panamá, Panamá
                </li>

                <li>
                  <a
                    href="https://www.instagram.com/viviconamoryarte/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 hover:text-primary transition-colors"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="#F3D6C6"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <rect width="20" height="20" x="2" y="2" rx="5" />
                      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                      <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
                    </svg>
                    Instagram: @viviconamoryarte
                  </a>
                </li>
                <li>
                  <a
                    href="https://www.tiktok.com/@viviconamoryarte7"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 hover:text-primary transition-colors"
                  >
                    <svg className="w-5 h-5" fill="#F3D6C6" viewBox="0 0 24 24">
                      <path d="M12.75 2h2.25a5.75 5.75 0 0 0 5.75 5.75v2.25a8 8 0 1 1-8-8v2.25a5.75 5.75 0 1 0 5.75 5.75h-2.25A3.5 3.5 0 1 1 9.25 5.75V2z" />
                    </svg>
                    TikTok: @viviconamoryarte7
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="font-playfair text-lg font-bold mb-4 text-[#F8F6F4]">
                Suscríbete
              </h3>
              <p className="text-[#F8F6F4]/90 text-base mb-4">
                Recibe nuestras novedades y promociones especiales.
              </p>
              <form
                className="flex flex-col gap-2"
                onSubmit={handleNewsletterSubmit}
              >
                <label htmlFor="footer-email" className="sr-only">
                  Correo electrónico
                </label>
                <div className="flex">
                  <input
                    id="footer-email"
                    type="email"
                    placeholder="Tu email"
                    required
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    disabled={isSubscribing}
                    className="px-4 py-2 w-full bg-[#F8F6F4] text-brown-light placeholder:text-brown-light/60 rounded-l-md focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  <Button
                    type="submit"
                    disabled={isSubscribing}
                    className="bg-primary text-brown-dark rounded-l-none hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubscribing ? "Enviando..." : "Enviar"}
                  </Button>
                </div>
              </form>
            </div>
          </div>

          {/* Métodos de pago — señal de confianza */}
          <div className="border-t border-[#F3D6C6]/30 mt-12 pt-8">
            <div className="flex flex-col items-center gap-4">
              <p className="text-[#F8F6F4]/80 text-sm font-medium tracking-wide">
                Paga seguro con
              </p>
              <div className="flex items-center gap-4">
                <span className="bg-white rounded-md px-3 py-1.5 shadow-sm flex items-center">
                  <img src="/payments/visa.svg" alt="Visa" className="h-3.5 w-auto" loading="lazy" />
                </span>
                <span className="bg-white rounded-md px-3 py-1.5 shadow-sm flex items-center">
                  <img src="/payments/mastercard.svg" alt="Mastercard" className="h-4 w-auto" loading="lazy" />
                </span>
                <span className="bg-white rounded-md px-3 py-1.5 shadow-sm flex items-center">
                  <img src="/payments/yappy.svg" alt="Yappy" className="h-4 w-auto" loading="lazy" />
                </span>
              </div>
            </div>
          </div>

          <div className="border-t border-[#F3D6C6]/30 mt-12 pt-8 text-center text-[#F8F6F4]/70 text-sm">
            <div className="flex flex-col md:flex-row justify-between items-center gap-2">
              <p>© 2026 Vivi con Amor y Arte. Todos los derechos reservados.</p>
              <p>
                Powered by{" "}
                <a
                  href="https://hooperits.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-primary transition-colors underline"
                >
                  HOOPERITS
                </a>{" "}
                &{" "}
                <a
                  href="https://bedoya.dev"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-primary transition-colors underline"
                >
                  Bedoya Dev
                </a>
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};
